import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Ship, Eye, EyeOff, ShoppingBag, Store } from "lucide-react";

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'buyer'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password) {
      toast.error('Please fill all fields');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      await register(formData.email, formData.password, formData.name, formData.role);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.message || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D4F8B] to-[#0A3D6B] flex items-center justify-center p-4" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-white rounded-sm flex items-center justify-center">
            <Ship className="w-7 h-7 text-[#0D4F8B]" />
          </div>
          <span className="font-bold text-2xl text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            AfriGulf Market
          </span>
        </div>

        <Card className="card-base">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">{t('auth.register.title')}</CardTitle>
            <CardDescription>{t('auth.register.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  {t('auth.name')}
                </label>
                <Input
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  aria-label="Full name"
                  className="input-style"
                  data-testid="register-name"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  {t('auth.email')}
                </label>
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  aria-label="Email"
                  className="input-style"
                  data-testid="register-email"
                />
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  {t('auth.password')}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    aria-label="Password"
                    className="input-style pr-10"
                    data-testid="register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-slate-700 mb-3 block">
                  {t('auth.role')}
                </label>
                <RadioGroup 
                  value={formData.role} 
                  onValueChange={(v) => setFormData({ ...formData, role: v })}
                  className="grid grid-cols-2 gap-4"
                >
                  <div>
                    <RadioGroupItem value="buyer" id="buyer" className="peer sr-only" />
                    <Label
                      htmlFor="buyer"
                      className="flex flex-col items-center justify-center p-4 border-2 rounded-md cursor-pointer peer-data-[state=checked]:border-[#0D4F8B] peer-data-[state=checked]:bg-[#0D4F8B]/5 hover:bg-slate-50 transition-colors"
                      data-testid="role-buyer"
                    >
                      <ShoppingBag className="w-6 h-6 mb-2 text-[#0D4F8B]" />
                      <span className="font-medium">{t('auth.role.buyer')}</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="supplier" id="supplier" className="peer sr-only" />
                    <Label
                      htmlFor="supplier"
                      className="flex flex-col items-center justify-center p-4 border-2 rounded-md cursor-pointer peer-data-[state=checked]:border-[#10B981] peer-data-[state=checked]:bg-[#10B981]/5 hover:bg-slate-50 transition-colors"
                      data-testid="role-supplier"
                    >
                      <Store className="w-6 h-6 mb-2 text-[#10B981]" />
                      <span className="font-medium">{t('auth.role.supplier')}</span>
                    </Label>
                  </div>
                </RadioGroup>
              </div>

              <Button 
                type="submit" 
                className="w-full bg-[#0D4F8B] hover:bg-[#0A3D6B]"
                disabled={loading}
                data-testid="register-submit"
              >
                {loading ? t('common.loading') : t('auth.register.cta')}
              </Button>
            </form>

            <p className="text-center text-sm text-slate-500 mt-6">
              {t('auth.hasAccount')}{' '}
              <Link to="/login" className="text-[#0D4F8B] hover:underline font-medium">
                {t('auth.loginLink')}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;

